import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Columns03, FilterBar, PageHeader, Select } from '@opendate-app/setlist';
import TrackingLinksTable from './TrackingLinksTable';
import { TRACKING_LINKS_COLUMNS } from '../constants/columnsConfig';
import useTableColumns from '../../ReportingDashboard/hooks/useTableColumns';
import TableSettings from '../../shared/components/TableSettings';
import CountBadge from "../../shared/components/CountBadge";
import '../styles/trackingLinks.css';

const axios = require('axios').default;
const qs = require('qs');

const SORT_OPTIONS = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'visits', label: 'Visits' },
  { value: 'orders', label: 'Orders' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'conversion_rate', label: 'Conversion' },
];

const cardStyle = {
  marginTop: 24,
  borderRadius: '12px',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  border: '1px solid var(--sl-border-secondary)',
  background: 'var(--sl-bg-primary)',
};
const filtersBarStyle = { padding: '12px 24px', gap: '12px' };

const summariseFilter = (filter, values) => {
  if (!values || values.length === 0) return 'Any';
  if (values.length === 1) return filter.options.find((option) => option.value === values[0])?.label ?? values[0];
  return `${values.length} selected`;
};

const TrackingLinksPage = ({ team, rows: initialRows, pagination: initialPagination, event_options: eventOptions }) => {
  const [rows, setRows] = useState(initialRows);
  const [pagination, setPagination] = useState(initialPagination);
  const [eventIds, setEventIds] = useState([]);
  const [sort, setSort] = useState('revenue');
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { visibleColumns, hiddenColumns, handleColumnsChange } = useTableColumns({
    columns: TRACKING_LINKS_COLUMNS,
    localStorageKey: (id) => `tracking_links_columns_v3_${id}`,
    teamId: team.id,
  });
  const visibleColumnIds = useMemo(() => visibleColumns.map((c) => c.id), [visibleColumns]);

  const latestRequest = useRef(0);

  const fetchPage = useCallback((page, opts = {}) => {
    const requestId = ++latestRequest.current;
    const params = { page, sort: opts.sort ?? sort, event_ids: opts.eventIds ?? eventIds };
    setLoading(true);
    axios.get(`/teams/${team.id}/tracking-links`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
      headers: { Accept: 'application/json' },
    }).then(({ data }) => {
      if (requestId !== latestRequest.current) return;
      setRows(data.rows);
      setPagination(data.pagination);
    }).finally(() => {
      if (requestId === latestRequest.current) setLoading(false);
    });
  }, [team.id, sort, eventIds]);

  const filters = useMemo(() => [
    {
      key: 'event',
      label: 'Event',
      type: 'search',
      options: eventOptions.map((e) => ({ value: String(e.id), label: e.name })),
    },
  ], [eventOptions]);
  const eventFilterValues = useMemo(() => (eventIds.length ? { event: eventIds.map(String) } : {}), [eventIds]);

  const onEventFilter = (next) => {
    const ids = (next.event || []).map(Number);
    setEventIds(ids);
    fetchPage(1, { eventIds: ids });
  };
  const onSort = (value) => { setSort(value); fetchPage(1, { sort: value }); };

  return (
    <div>
      <PageHeader
        title={(
          <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
            Tracking Links
            <CountBadge count={pagination.total_entries} />
          </span>
        )}
        actions={(
          <div className="tl-columns-control position-relative">
            <Button icon={Columns03} onClick={() => setSettingsOpen((o) => !o)}>Columns</Button>
            <TableSettings
              isOpen={settingsOpen}
              onClose={() => setSettingsOpen(false)}
              visibleFields={visibleColumns}
              hiddenFields={hiddenColumns}
              onFieldsChange={handleColumnsChange}
            />
          </div>
        )}
      />
      <div className="tl-page" style={cardStyle}>
        <div className="d-flex align-items-center" style={filtersBarStyle}>
          <div className="flex-grow-1">
            <FilterBar
              variant="plain"
              filters={filters}
              values={eventFilterValues}
              onChange={onEventFilter}
              alwaysShow={['event']}
              formatValue={summariseFilter}
              onClearAll={() => onEventFilter({})}
            />
          </div>
          <div data-testid="tl-sort">
            <Select size="sm" width={180} value={sort} disabled={loading} options={SORT_OPTIONS} onChange={onSort} />
          </div>
        </div>

        <TrackingLinksTable rows={rows} visibleColumnIds={visibleColumnIds} />

        <div className="tl-pagination d-flex align-items-center justify-content-between">
          <span className="figma-text sm">
            {pagination.total_entries} link{pagination.total_entries === 1 ? '' : 's'}
          </span>
          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
            <button type="button" className="btn btn-setlist-secondary btn-sm tl-prev"
              disabled={loading || pagination.current_page <= 1}
              onClick={() => fetchPage(pagination.current_page - 1)}>Previous</button>
            <span className="figma-text sm">Page {pagination.current_page} of {pagination.total_pages}</span>
            <button type="button" className="btn btn-setlist-secondary btn-sm tl-next"
              disabled={loading || pagination.current_page >= pagination.total_pages}
              onClick={() => fetchPage(pagination.current_page + 1)}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};

TrackingLinksPage.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  rows: PropTypes.array.isRequired,
  pagination: PropTypes.object.isRequired,
  event_options: PropTypes.array.isRequired,
};

export default TrackingLinksPage;
